/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

'use strict';

// tslint:disable: only-arrow-functions
// tslint:disable: no-redundant-jsdoc

import { logger } from '@touchplan/logging';

declare const window;

if (Map) {
	(<any> Map.prototype).first = function() {
		logger.log('[basic-utils] [Map.first] debug code, don\'t use');
		return this.size > 0 ? this.values().next().value : null;
	};
}

//pads 0s
//n - number, width - number to pad, z - character to pad
export function pad(n, width, z?) {
	z = z || '0';
	n = n + '';
	return n.length >= width ? n : new Array(width - n.length + 1).join(z) + n;
}

// a proper modulo that handles negative numbers
export function properMod(n, m) {
	return ((n % m) + m) % m;
}

export function clipString(string, length?) {
	if (!length) { length = 50; }
	if (!string) { return ''; }
	if (string.length > length) { return string.substring(0, length) + '...'; }
	return string;
}

export function makeStack<T extends { stack?: unknown; description?: unknown; } >(error: T) {
	const extra: any = { };
	try { throw error; }
	catch (e) {
		extra.stack = (e as T)?.stack || null;
		extra.description = (e as T)?.description || null;
	}
	return extra;
}

// Bind a context to a function
export function bind(ctx, fn) {
	return function() { return fn.apply(ctx, arguments); };
}

/*
	Usage:
	return sortedArr.sort(sortBy(<key>, false, <parseFloat, getDate, etc>));
 */
export function sortBy(field, reverse, primer) {
	if (primer === 'date') { primer = function(dateString) { return new Date(dateString).getTime(); }; }
	const key = primer ? function (x) {
		return primer(x[field]);
	} : function (x) {
		return x[field];
	};
	reverse = !reverse ? 1 : -1;
	return function (c, d) {
		const a = key(c);
		const b = key(d);
		return reverse * ((a > b) as any - ((b > a) as any));
	};
}

//### debug()
// Goal will be to migrate all debug functions/ values to use this
//* debug.add(key, val) - added a new value to the debug list
//* debug.* - all values added are exposed on the debug list
export const debug = (function() {
	const enabled = true;
	const bucket: any = { };
	const supportList = ['disableRequestDeletionOnSuccess', 'cullTickets', 'disableCompletedCheck'];

	bucket.add = add;

	function add(key, val) {
		if (!enabled) { return null; }
		if (key === undefined || key === null) { return null; }
		if (supportList.indexOf(key) === -1) { return null; }

		bucket[key] = val;
	}
	return bucket;
})();
window.debug = debug;

// Creates a map of error messages with a timeStamp. If new timeStamp is too recent compared to old timestamp, no cally callback.
export function throttleError( fn, difference? ) {
	const map = { };
	const dif = difference || -10000;
	return function( message, severity? ) {
		const timeStamp = new Date().getTime();
		if ( map[ message ] && ( map[ message ] - timeStamp ) > dif ) { return; }
		else if ( map[ message ] ) {
			map[ message ] = timeStamp;
			fn.apply( null, arguments );
			return;
		}
		// No error mapped. Add it to map.
		map[ message ] = timeStamp;
		fn.apply( null, arguments );
	};
}

// Run a fn repeatedy at most once per <interval> milliseconds
export function throttle(fn, interval?) {
	interval = interval || 100;
	let timer = null;
	return function () {
		const context = this;
		const args = arguments;
		if (timer) { return; } else {
			timer = setTimeout(function () {
				fn.apply(context, args);
				timer = false;
			}, interval);
		}
	};
}

/**
 * Debounce a function so repeated calls that happen in less then <delay>
 * millesconds get squelched
 * @param  {Function}  fn [function to be debounced]
 * @param  {number =  1000}        delay [delay]
 * @return {Function}     [function you actually call]
 */
export function soak(fn: Function, delay: number = 1000): Function {
	let timer = null;
	return function () {
		const context = this;
		const args = arguments;
		clearTimeout(timer);
		timer = setTimeout(function () {
			fn.apply(context, args);
		}, delay);
	};
}

export function relationIterator(target, cb, context?) {
	if (!target || typeof target !== 'object') { return; }
	if (target.data) { target = target.data; }
	const cessor = 'predecessors';
	$loop(target[cessor], function(plan, planId) {
		$loop(plan, function(__, otherTicketId) {
			cb.call(context, {
				id: otherTicketId,
				planId,
				cessor,
			});
		});
	});
}

//### getNested(obj, key)
// returns the value of obj[key] taking into account any potential nesting of key.
// Nesting being speced as "part1.part2.part3"
export function getNested(obj, key) {
	return key.split('.').reduce(function(o, x) {
			return (typeof o === 'undefined' || o === null) ? o : o[x];
	}, obj);
}

export function checkNested(obj, ...theRest /*, level1, level2, ... levelN*/) {
	// tslint:disable-next-line: prefer-for-of
	for (let i = 0; i < theRest.length; i++) {
		if (!obj.hasOwnProperty(theRest[i])) { return false; }
		obj = obj[theRest[i]];
	}
	return true;
}

/** special variant of checkNested that considers a null-y value to not be there */
export function checkNestedOrNull(obj, ...theRest /*, level1, level2, ... levelN*/) {
	// tslint:disable-next-line: prefer-for-of
	for (let i = 0; i < theRest.length; i++) {
		if (!obj.hasOwnProperty(theRest[i]) || obj[theRest[i]] === undefined || obj[theRest[i]] === null) { return false; }
		obj = obj[theRest[i]];
	}
	return true;
}

/**
 * set a deeply nested value, creating the tree as necessary
 * @param  obj        The object to write to
 * @param  value      The value to be written
 * @param  ...theRest Key names to nest with
 */
export function setNested(obj, value, ...theRest /*, level1, level2, ... levelN*/) {
	let iter = obj;
	for (let i = 0; i < theRest.length; i++) {
		if (!iter[theRest[i]]) { iter[theRest[i]] = { }; }
		if (i < theRest.length - 1) { iter = iter[theRest[i]]; }
		else { iter[theRest[i]] = value; }
	}
	iter = value;
}

export function checkNestedProto(obj, ...theRest /*, level1, level2, ... levelN*/) {
	// tslint:disable-next-line: prefer-for-of
	for (let i = 0; i < theRest.length; i++) {
		if (!obj[theRest[i]]) { return false; }
		obj = obj[theRest[i]];
	}
	return true;
}

export function $loop(obj, fn, ctx?) {
	for (const i in obj) {
		if (!(i.charAt(0) === '$') && obj.hasOwnProperty(i)) {
			fn.call(ctx, obj[i], i, obj);
		}
	}
}

export const translateToGerman = (function() {
	const sectionsToChange = [
		'.report-controller .left-container',
		'.report-controller .center-container',
		'.weekly-work-report-table thead',
		'.sixweek-header',
		'.gantt-tbl thead',
		'.ppc-by-week-tbl thead',
		'.constraint-log-table thead'
	];

	const replacements = [
		{ orig: /^Plans/, replacement: 'Pläne'},
		{ orig: /^Roles/, replacement: 'Gewerke'},
		{ orig: /^Locations/, replacement: 'Standorte'},
		{ orig: /^Start Date/, replacement: 'Startdatum'},
		{ orig: /^End Date/, replacement: 'Enddatum'},
		{ orig: 'Data as of', replacement: 'Daten per'},
		{ orig: 'Weeks Commencing', replacement: 'Woche beginnend'},
		{ orig: 'Week Commencing', replacement: 'Woche beginnend'},
		{ orig: 'Task Description', replacement: 'Aufgabenbeschreibung'},
		{ orig: 'All Plans', replacement: 'Alle Pläne'},
		{ orig: 'All Roles', replacement: 'Alle Gewerke'},
		{ orig: 'All Locations', replacement: 'Alle Standorte'},
		{ orig: 'Prerequisite Work', replacement: 'Notwendige Vorraussctzung'},
		{ orig: 'Project Roles', replacement: 'Projekt Gewerke'},
		{ orig: 'Prior?', replacement: 'vorherige woche?'},
		{ orig: 'Crew Size', replacement: 'Kolonnengröße'},
		{ orig: 'Next', replacement: 'nächste woche'},
		{ orig: 'Complete', replacement: 'erledigt'},
		{ orig: 'Met Promise', replacement: 'Zusage eingehalten'},
		{ orig: 'Reason for Variance', replacement: 'Grund für Abweichung'},
		{ orig: 'Progress Notes', replacement: 'Fortschrittsbemerkung'},
		{ orig: 'Notes', replacement: 'Bemerkungen'},
		{ orig: 'Constraints', replacement: 'Einschränkungen'},
		{ orig: 'Reason For Variance', replacement: 'Grund für Abweichung'},
		{ orig: 'Six Week Look Ahead', replacement: '6-Wochenvorschau'},
		{ orig: 'Gantt Project Report', replacement: 'Balkenplan'},
		{ orig: 'Days', replacement: 'Tage'},
		{ orig: 'Planned Start', replacement: 'Geplanter Anfang'},
		{ orig: 'Planned Finish', replacement: 'Geplantes Ende'},
		{ orig: 'Actual Finish', replacement: 'Tatsächliches Ende'},
		{ orig: 'Bold italic indicates a constraint', replacement: 'Fett kursiv bedeutet eine Einschränkung'},
		{ orig: 'PPC By Week', replacement: 'Wöchentlicher PEA Wert'},
		{ orig: 'Promises Met', replacement: 'Zusage eingehalten'},
		{ orig: 'Promises Made', replacement: 'gemachte Zusagen'},
		{ orig: 'Combined', replacement: 'verbunden'},
		{ orig: 'PPC by Variance Reason', replacement: 'PEA Nach Abweichungsgrund'},
		{ orig: 'PPC by Variance', replacement: 'PEA Nach Abweichungsgrund'},
		{ orig: 'PPC Variance By Role', replacement: 'Fett kursiv bedeutet eine Einschränkung'},
		{ orig: 'Constraint Description', replacement: 'Beschreibung der Einschränkung'},
		{ orig: 'Constrained Tasks', replacement: 'Einschränkende Aufgaben'},
		{ orig: 'Date Identified', replacement: 'Tag der Feststellung'},
		{ orig: 'Date Promised', replacement: 'Zugesagtes Datum'},
		{ orig: 'Reponsible Party', replacement: 'Verantwortlicher'},
		{ orig: 'Notes', replacement: 'Tag der Feststellung'},
		{ orig: 'Current Plan', replacement: 'Aktueller Plan'},
		{ orig: 'Original Milestone', replacement: 'Original Meilenstein'},
		{ orig: 'Days current schedule is before or after milestone', replacement: 'Aktueller Plan ist vor oder nach Meilenstein'},
		{ orig: 'Work Days', replacement: 'Arbeitstage'},
		{ orig: 'Required Date', replacement: 'Zugesagtes Datum'},
		{ orig: 'Planned Date', replacement: 'Geplantes Datum'},
		{ orig: 'Responsible Project Member', replacement: 'Verantwortlicher'},
		{ orig: 'Assigned To', replacement: 'Verantwortlicher'},
		{ orig: 'Week', replacement: 'Woche'}
	];

	const walkDOM = function (node) {
		if (!node) { return; }
		else if (node.hasChildNodes()) {
			node.childNodes.forEach(function(childNode) { walkDOM(childNode); });
		}
		else {
			if (node.nodeType === 3) { //text
				const s = node.nodeValue.trim();
				if (!s) { return; }

				replacements.forEach(function(obj) {
					node.nodeValue = node.nodeValue.replace(obj.orig, obj.replacement);
				});
			}
		}
	};

	function walkDOMList(list) {
		list.forEach(function(selector) {
			walkDOM(document.querySelector(selector));
		});
	}

	return function() { walkDOMList(sectionsToChange); };
})();

/**
 * Might as well just have a general use toggle fuction...
 * @param  {boolean} target [the existing property]
 * @param  {boolean} force  [an optional force arguement]
 * @return {boolean}        [flipped]
 */
export function toggle(target: boolean, force?: boolean): boolean {
	if (force !== undefined) { return force; }
	else { return !target; }
}

/**
 * From https://github.com/mreinstein/remove-array-items
 * A faster implementation of splice that doesn't generate garbage
 * @param arr
 * @param startIdx
 * @param removeCount
 */
export function fastSplice(arr, startIdx, removeCount) {
	let i;
	const length = arr.length;

	if (startIdx >= length || removeCount === 0) {
		return;
	}

	removeCount = (startIdx + removeCount > length ? length - startIdx : removeCount);

	const len = length - removeCount;

	for (i = startIdx; i < len; ++i) {
		arr[i] = arr[i + removeCount];
	}

	arr.length = len;
}

// export function flattenArray(arr: any[] = []){
// 	return arr.reduce((flat, toFlatten) => {
// 		return flat.concat(Array.isArray(toFlatten) ? flattenArray(toFlatten) : toFlatten);
// 	}, []);
// }
export function flattenArray(arr: any[] = []) {
	const flattened = [];

	arr.forEach((thing) => {
		thing.forEach((val) => {
			flattened.push(val);
		});
	});

	return flattened;
}

/**
 * This utility throttles events against the provided object and only dispatches one event of said type each frame.
 *
 * This is used to throttle 'resize' in the canvas renderer, since resizing the canvas renderer can be expensive,
 * we don't want to do it multiple times each tick.
 * @param {string} type The event name to listen for
 * @param {string} name The new event name to dispatch once one tick has elapsed.
 * @param {any} obj The thing to listen for events.
 */
export function throttleEvent(type: string, name: string, obj?: any) {
	obj = obj || window;
	let running = false;
	let cancelRequest;
	const func = () => {
		if (running) { return; }
		running = true;
		cancelRequest = requestAnimationFrame(function() {
			obj.dispatchEvent(new CustomEvent(name));
			running = false;
		});
	};
	obj.addEventListener(type, func);
}

/**
 * Method to clamp a number to a min and max value
 * @param num The number to clamp
 * @param min The minimum value
 * @param max The maximum value
 * @returns {any}
 */
export function clamp(num: number, min: number, max: number = Number.MAX_VALUE) {
	return num <= min ? min : num >= max ? max : num;
}

/**
 * Method to clamp a number between 0 and 1
 * @param {number} num
 */
export function clamp01(num: number): number {
	return clamp(num, 0, 1);
}

/**
 * Method to interpolate between two numbers
 * @param {number} a
 * @param {number} b
 */
export function interpolate(a: number, b: number) {
	return function(t) { return a * (1 - t) + b * t; };
}

/**
 * Reverse of interpolate
 * @param {number} a
 * @param {number} b
 */
export function uninterpolate(a: number, b: number) {
	b = (b -= a) || 1 / b;
	return function(x) { return (x - a) / b; };
}

/**
 * Returns a random number. Tyler uses this to generate dummy data
 * @param {number} min Min value
 * @param {number} max Max value
 */
export function randomNumber(min: number, max: number): number {
	return Math.floor((Math.random() * max) + min);
}

export function essentiallyEquals(a: number, b: number, threshold = 0.5) {
	return Math.abs(b - a) < threshold;
}

// parseUrl('/foo/bar/baz', './qux');
// //=> /foo/bar/baz/qux
//
// parseUrl('/foo/bar/baz', '../qux');
// //=> /foo/bar/qux
//
// parseUrl('/foo/bar/baz', '../../qux');
// //=> /foo/qux
//
// parseUrl('/foo/bar/baz', '..');
// //=> /foo/bar
export function parseUrl(url: string, redirectTo: string) {
	const urlTokens = url.split('/');
	const redirectToTokens = redirectTo.split('/');

	let token = redirectToTokens.shift();

	while (token) {
		if (token !== '.' && token !== '..') {
			redirectToTokens.unshift(token);
			break;
		}

		if (token === '..') {
			urlTokens.pop();
		}

		token = redirectToTokens.shift();
	}

	urlTokens.push(...redirectToTokens);

	return urlTokens.join('/');
}

export function concatMaps(map, ...iterables) {
	if (!iterables || !iterables.length) { return map; }
	for (const iterable of iterables) {
		if (!iterable) { continue; }
		for (const item of iterable) {
			map.set(...item);
		}
	}
}

export function sortListAlphabetically(list: any[], sortKey: string): any[] {
	return list.sort((a, b) => {
		const firstName: string = a[sortKey]?.toLowerCase() || '';
		const secondName: string = b[sortKey]?.toLowerCase() || '';
		return firstName.localeCompare(secondName);
	});
}

export function runInRootZone(cb) {
	return Zone.root.run(cb);
}
